import { Spinner } from "@via/components/loading/loading.tsx";
import { forgotPasswordOptions } from "@via/utils/form-validators.tsx";
import { type ForgotPasswordPayload } from "@via/utils/interfaces.tsx";
import { type JSX } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { useForgotPassword } from "@via/api/auth.api.tsx";
import Logo from "/logo.svg";

export default function ResetPassword(): JSX.Element {
  const { mutate: forgotPassword, isPending: isForgotPasswordLoading } =
    useForgotPassword();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordPayload>({ mode: "onChange" });

  const handleResetPassword = (form: ForgotPasswordPayload): void => {
    forgotPassword(form);
  };

  return (
    <>
      <div className="dotted flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="text-center sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/">
            <img
              className="mx-auto h-10 w-auto duration-200 ease-in-out hover:rotate-180"
              src={Logo}
              alt="Your Company"
            />
          </Link>
          <h2 className="via-h2 mt-6 text-2xl font-bold leading-9 tracking-tight">
            Having trouble logging in?
          </h2>
          <p className="mt-4">
            Enter your e-mail and we&apos;ll send you a recovery link
          </p>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <form
              className="space-y-6"
              onSubmit={e => {
                e.preventDefault();
                void handleSubmit(handleResetPassword)(e);
              }}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-6 text-gray-900">
                  Email address <span className="text-red-400">*</span>
                </label>
                <div className="mt-2">
                  <input
                    {...register("email", forgotPasswordOptions.email)}
                    autoComplete="off"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 duration-200 ease-in-out placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                  {errors.email && (
                    <span className="text-xs text-rose-500">
                      {errors.email.message}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isForgotPasswordLoading}
                  className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  <Spinner loading={isForgotPasswordLoading}>
                    Send Recovery Email
                  </Spinner>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
